import { useState } from 'react';
import {
  ExternalLink,
  Star,
  Archive,
  Edit2,
  Trash2,
  MoreVertical,
  MessageSquare,
  RefreshCw,
  Eye,
  Bot,
  Wand2,
  Loader2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { bookmarksApi } from '../lib/api';
import { extractDomain, getFaviconUrl } from '../lib/metadata';
import { getIconByName } from '../lib/icons';
import { quickEnrich } from '../lib/bookmark-enrichment';
import { isAIConfigured, generateAITags, generateAISummary } from '../lib/ai';
import { TagInput } from './TagInput';
import { MetadataCompareDialog } from './MetadataCompareDialog';
import { BookmarkDetailDialog } from './BookmarkDetailDialog';
import { useTags } from '../hooks/use-tags';
import type { BookmarkWithTags, Collection } from '../types/database';

interface FetchedMetadata {
  title: string;
  description: string;
  newTags: string[];
  // Extended metadata
  content?: string | null;
  excerpt?: string | null;
  author?: string | null;
  site_name?: string | null;
  published_at?: string | null;
  reading_time?: number | null;
  thumbnail?: string | null;
  // AI generated content
  aiSummary?: string | null;
}

interface CompareDialogState {
  open: boolean;
  fetched: FetchedMetadata | null;
  loading: boolean;
}

interface BookmarkCardProps {
  bookmark: BookmarkWithTags;
  collections: Collection[];
  onUpdate: () => void;
  disabled?: boolean;
}

export function BookmarkCard({ bookmark, collections, onUpdate, disabled = false }: BookmarkCardProps) {
  const [editOpen, setEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState(bookmark.title);
  const [description, setDescription] = useState(bookmark.description || '');
  const [notes, setNotes] = useState(bookmark.notes || '');
  const [collectionId, setCollectionId] = useState(bookmark.collection_id || '');
  const [tags, setTags] = useState(bookmark.tags.map((t) => t.name).join(', '));
  const [compareDialog, setCompareDialog] = useState<CompareDialogState>({
    open: false,
    fetched: null,
    loading: false,
  });

  const { data: existingTags = [] } = useTags();

  const handleToggleFavorite = async () => {
    try {
      await bookmarksApi.update(bookmark.id, { is_favorite: !bookmark.is_favorite });
      onUpdate();
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      toast.error('문제가 생겼어요');
    }
  };

  const handleToggleArchive = async () => {
    try {
      await bookmarksApi.update(bookmark.id, { is_archived: !bookmark.is_archived });
      toast.success(bookmark.is_archived ? '보관 해제했어요' : '보관했어요');
      onUpdate();
    } catch (error: any) {
      console.error('Error toggling archive:', error);
      toast.error('문제가 생겼어요');
    }
  };

  const handleDelete = async () => {
    if (!confirm('삭제할까요?')) return;

    try {
      await bookmarksApi.delete(bookmark.id);
      toast.success('삭제했어요');
      onUpdate();
    } catch (error: any) {
      console.error('Error deleting bookmark:', error);
      toast.error('삭제에 문제가 생겼어요');
    }
  };

  const handleRefreshMetadata = async () => {
    const toastId = 'refresh-metadata';

    try {
      toast.loading('정보를 가져오는 중...', { id: toastId });

      // Use quickEnrich for metadata + auto tags only (no AI)
      const currentTags = bookmark.tags.map((t) => t.name);
      const result = await quickEnrich(bookmark.url);

      // Filter out existing tags
      const existingLower = new Set(currentTags.map((t) => t.toLowerCase()));
      const newTags = result.allTags.filter((t) => !existingLower.has(t.toLowerCase()));

      toast.dismiss(toastId);

      // Open comparison dialog with enriched data
      setCompareDialog({
        open: true,
        fetched: {
          title: result.metadata.title || '',
          description: result.metadata.description || '',
          newTags,
          // Extended metadata
          content: result.metadata.content || null,
          excerpt: result.metadata.excerpt || null,
          author: result.metadata.author || null,
          site_name: result.metadata.site_name || null,
          published_at: result.metadata.published_at || null,
          reading_time: result.metadata.reading_time || null,
          thumbnail: result.metadata.thumbnail || null,
          // No AI summary in refresh metadata
          aiSummary: null,
        },
        loading: false,
      });

      toast.success('정보를 가져왔어요');
    } catch (error) {
      console.error('Error refreshing metadata:', error);
      const axiosError = error as { response?: { data?: { error?: string } } };
      const errorMessage = axiosError.response?.data?.error || '정보 새로고침에 실패했어요';
      toast.error(errorMessage, { id: toastId });
    }
  };

  const handleGenerateAITags = async () => {
    if (!isAIConfigured()) {
      toast.error('AI 설정이 필요합니다. 설정에서 API 키를 입력해주세요.');
      return;
    }

    const toastId = 'ai-tags';
    try {
      toast.loading('AI가 태그를 분석하는 중...', { id: toastId });

      const currentTags = bookmark.tags.map((t) => t.name);
      const result = await generateAITags(
        bookmark.url,
        bookmark.title,
        bookmark.description || '',
        currentTags
      );

      toast.dismiss(toastId);

      if (result.tags.length > 0) {
        // Filter out existing tags
        const existingLower = new Set(currentTags.map((t) => t.toLowerCase()));
        const newTags = result.tags.filter((t) => !existingLower.has(t.toLowerCase()));

        if (newTags.length > 0) {
          // Update bookmark with new tags
          const mergedTags = [...currentTags, ...newTags];
          await bookmarksApi.update(bookmark.id, { tags: mergedTags });
          toast.success(`AI가 ${newTags.length}개의 태그를 추가했어요`);
          onUpdate();
        } else {
          toast.info('AI가 추가할 새 태그를 찾지 못했어요');
        }
      } else {
        toast.info('AI가 추가 태그를 찾지 못했어요');
      }
    } catch (error: any) {
      console.error('AI tagging failed:', error);
      toast.error(error.message || 'AI 태그 생성에 실패했어요', { id: toastId });
    }
  };

  const handleGenerateAISummary = async () => {
    if (!isAIConfigured()) {
      toast.error('AI 설정이 필요합니다. 설정에서 API 키를 입력해주세요.');
      return;
    }

    const toastId = 'ai-summary';
    try {
      toast.loading('AI가 요약을 생성하는 중...', { id: toastId });

      const result = await generateAISummary(
        bookmark.url,
        bookmark.title,
        bookmark.description || '',
        bookmark.content || undefined
      );

      toast.dismiss(toastId);

      if (result.summary) {
        // Update bookmark with AI summary
        await bookmarksApi.update(bookmark.id, { excerpt: result.summary });
        toast.success('AI가 요약을 생성했어요');
        onUpdate();
      } else {
        toast.info('AI가 요약을 생성하지 못했어요');
      }
    } catch (error: any) {
      console.error('AI summary failed:', error);
      toast.error(error.message || 'AI 요약 생성에 실패했어요', { id: toastId });
    }
  };

  const handleCompareConfirm = async (
    selections: Record<string, 'old' | 'new'>,
    selectedNewTags: string[]
  ) => {
    if (!compareDialog.fetched) return;

    setCompareDialog((prev) => ({ ...prev, loading: true }));

    try {
      const currentTags = bookmark.tags.map((t) => t.name);
      const mergedTags = [...currentTags, ...selectedNewTags];

      // Base update data
      const updateData: Record<string, unknown> = {
        favicon_url: getFaviconUrl(bookmark.url),
        domain: extractDomain(bookmark.url),
        tags: mergedTags,
        thumbnail_url: compareDialog.fetched.thumbnail,
      };

      // Apply selected metadata changes based on user selection
      if (selections.title === 'new' && compareDialog.fetched.title) {
        updateData.title = compareDialog.fetched.title;
      }
      if (selections.description === 'new') {
        updateData.description = compareDialog.fetched.description || null;
      }
      if (selections.excerpt === 'new') {
        updateData.excerpt = compareDialog.fetched.excerpt || null;
      }
      if (selections.author === 'new') {
        updateData.author = compareDialog.fetched.author || null;
      }
      if (selections.site_name === 'new') {
        updateData.site_name = compareDialog.fetched.site_name || null;
      }
      if (selections.published_at === 'new') {
        updateData.published_at = compareDialog.fetched.published_at || null;
      }
      if (selections.reading_time === 'new') {
        updateData.reading_time = compareDialog.fetched.reading_time || null;
      }
      if (selections.content_preview === 'new') {
        updateData.content = compareDialog.fetched.content || null;
      }

      await bookmarksApi.update(bookmark.id, updateData);
      toast.success('정보를 업데이트했어요');

      setCompareDialog({ open: false, fetched: null, loading: false });
      onUpdate();
    } catch (error) {
      console.error('Error applying changes:', error);
      toast.error('변경 적용에 문제가 생겼어요');
      setCompareDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare tags array
      const tagsList = tags.trim()
        ? tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        : [];

      // Update bookmark via API
      await bookmarksApi.update(bookmark.id, {
        title,
        description: description || null,
        notes: notes || null,
        collection_id: collectionId && collectionId !== 'none' ? collectionId : null,
        tags: tagsList,
      });

      toast.success('수정했어요');
      setEditOpen(false);
      onUpdate();
    } catch (error: any) {
      console.error('Error updating bookmark:', error);
      toast.error('수정에 문제가 생겼어요');
    } finally {
      setLoading(false);
    }
  };

  const renderCollectionIcon = (collection: Collection) => {
    const IconComponent = getIconByName(collection.icon || 'folder');
    return (
      <IconComponent
        className="mr-1 inline-block h-4 w-4"
        style={{ color: collection.color || '#10b981' }}
      />
    );
  };

  return (
    <>
      <article className="group flex h-[130px] overflow-hidden rounded-lg border border-border bg-card transition-all hover:border-primary/30 hover:shadow-md">
        <div className="flex flex-1 items-start gap-2.5 overflow-hidden p-3">
          {bookmark.favicon_url && (
            <img
              src={bookmark.favicon_url}
              alt=""
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-sm"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h3
                  className="cursor-pointer truncate text-sm font-medium leading-tight text-foreground transition-colors hover:text-primary"
                  onClick={() => setDetailOpen(true)}
                >
                  {bookmark.title}
                </h3>
                <a
                  href={bookmark.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-0.5 inline-flex max-w-full items-center gap-1 truncate text-xs text-muted-foreground transition-colors hover:text-primary"
                >
                  <span className="truncate">{bookmark.domain}</span>
                  <ExternalLink className="h-3 w-3 flex-shrink-0" />
                </a>
              </div>

              <div className="-mt-0.5 flex items-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={handleToggleFavorite}
                >
                  <Star
                    className={`h-3.5 w-3.5 transition-colors ${
                      bookmark.is_favorite ? 'fill-primary text-primary' : ''
                    }`}
                  />
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setDetailOpen(true)}>
                      <Eye className="mr-2 h-3.5 w-3.5" />
                      상세 보기
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setEditOpen(true)}>
                      <Edit2 className="mr-2 h-3.5 w-3.5" />
                      수정
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleRefreshMetadata}>
                      <RefreshCw className="mr-2 h-3.5 w-3.5" />
                      정보 새로고침
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleGenerateAITags}>
                      <Bot className="mr-2 h-3.5 w-3.5" />
                      AI 태그 생성
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleGenerateAISummary}>
                      <Wand2 className="mr-2 h-3.5 w-3.5" />
                      AI 요약 생성
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleToggleArchive}>
                      <Archive className="mr-2 h-3.5 w-3.5" />
                      {bookmark.is_archived ? '보관 해제' : '보관'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                      <Trash2 className="mr-2 h-3.5 w-3.5" />
                      삭제
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {bookmark.description && (
              <p className="mt-1.5 line-clamp-2 text-xs text-muted-foreground">
                {bookmark.description}
              </p>
            )}

            {bookmark.notes && (
              <div className="mt-2 flex items-start gap-1.5 rounded bg-accent/50 px-2 py-1.5 text-xs text-accent-foreground">
                <MessageSquare className="mt-0.5 h-3 w-3 flex-shrink-0" />
                <span className="line-clamp-2">{bookmark.notes}</span>
              </div>
            )}

            {bookmark.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {bookmark.tags.map((tag) => (
                  <Badge key={tag.id} variant="secondary" className="h-5 px-1.5 py-0 text-[10px]">
                    {tag.name}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        {bookmark.thumbnail_url && (
          <div className="aspect-square h-[130px] flex-shrink-0">
            <img
              src={bookmark.thumbnail_url}
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        )}
      </article>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>북마크 수정</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleEdit} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-title">제목 *</Label>
              <Input
                id="edit-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">설명</Label>
              <Textarea
                id="edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-collection">컬렉션</Label>
              <Select value={collectionId} onValueChange={setCollectionId}>
                <SelectTrigger>
                  <SelectValue placeholder="선택 (선택사항)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">없음</SelectItem>
                  {collections.map((collection) => (
                    <SelectItem key={collection.id} value={collection.id}>
                      {renderCollectionIcon(collection)}
                      {collection.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-tags">태그</Label>
              <TagInput
                value={tags}
                onChange={setTags}
                existingTags={existingTags}
                placeholder="태그 입력 또는 선택"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-notes">메모</Label>
              <Textarea
                id="edit-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                취소
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? '저장 중...' : '저장'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <MetadataCompareDialog
        open={compareDialog.open}
        onOpenChange={(open) => setCompareDialog((prev) => ({ ...prev, open }))}
        title="정보 새로고침"
        fields={
          compareDialog.fetched
            ? [
                {
                  key: 'title',
                  label: '제목',
                  oldValue: bookmark.title,
                  newValue: compareDialog.fetched.title,
                },
                {
                  key: 'description',
                  label: '설명',
                  oldValue: bookmark.description || '',
                  newValue: compareDialog.fetched.description,
                },
                {
                  key: 'excerpt',
                  label: '발췌',
                  oldValue: bookmark.excerpt || '',
                  newValue: compareDialog.fetched.excerpt || '',
                },
                {
                  key: 'author',
                  label: '저자',
                  oldValue: bookmark.author || '',
                  newValue: compareDialog.fetched.author || '',
                },
                {
                  key: 'site_name',
                  label: '사이트',
                  oldValue: bookmark.site_name || '',
                  newValue: compareDialog.fetched.site_name || '',
                },
                {
                  key: 'published_at',
                  label: '발행일',
                  oldValue: bookmark.published_at || '',
                  newValue: compareDialog.fetched.published_at || '',
                },
                {
                  key: 'reading_time',
                  label: '읽기 시간',
                  oldValue: bookmark.reading_time ? `${bookmark.reading_time}분` : '',
                  newValue: compareDialog.fetched.reading_time ? `${compareDialog.fetched.reading_time}분` : '',
                },
                {
                  key: 'content_preview',
                  label: '본문 미리보기',
                  oldValue: bookmark.content ? `${bookmark.content.substring(0, 200)}...` : '',
                  newValue: compareDialog.fetched.content ? `${compareDialog.fetched.content.substring(0, 200)}...` : '',
                },
              ]
            : []
        }
        tagsField={
          compareDialog.fetched
            ? {
                currentTags: bookmark.tags.map((t) => t.name),
                newTags: compareDialog.fetched.newTags,
              }
            : undefined
        }
        onConfirm={handleCompareConfirm}
        loading={compareDialog.loading}
      />

      <BookmarkDetailDialog bookmark={bookmark} open={detailOpen} onOpenChange={setDetailOpen} />
    </>
  );
}
